/**
 * @fileoverview Shared confirm-and-bind modal for merge-field placeholders detected when AI
 * recreates a document from an upload (G-AI-UX-01).
 * @module ui-admin/components/RecreatePlaceholderReviewModal
 *
 * Responsibilities: - Present every detected {{placeholder}} so the user can keep it, rename it,
 *                     or bind it to a known inspection merge field before it lands in the canvas.
 * Dependencies:     - React, lucide-react icons, @firesquirrel/document-render (merge-field catalog).
 * Architecture:     - Pure presentational modal; no data access. The caller applies the returned
 *                     rename map to its DocumentLayout (via renamePlaceholdersInLayout) and persists.
 *                     Used by BOTH tenant-web and system-web builder pages so the UX is identical.
 * Notes:            - Replaces the prior window.alert()-only confirmation. Renaming a detected token
 *                     to a catalog token IS the binding action in the {{mergeField}} token model.
 */
import React, { useMemo, useState } from 'react';
import { X, Wand2, Check } from 'lucide-react';
import { DOCUMENT_MERGE_FIELDS, mergeFieldToken } from '@firesquirrel/document-render';

export interface RecreatePlaceholderReviewModalProps {
  /** Whether the modal is shown. */
  open: boolean;
  /** Name of the uploaded source file (for context in the header). */
  fileName?: string;
  /** Distinct detected placeholder token names (without braces). */
  placeholders: string[];
  /** Apply the confirmed rename map (oldToken → newToken); unchanged tokens are omitted. */
  onConfirm: (rename: Record<string, string>) => void;
  /** Dismiss without applying any rename. */
  onClose: () => void;
}

/** True when `name` is a valid bare token identifier. */
function isValidToken(name: string): boolean {
  return /^[a-zA-Z0-9_]+$/.test(name);
}

/**
 * @component RecreatePlaceholderReviewModal
 * Purpose:     Let the user review/rename/bind the merge-field placeholders an AI recreate detected.
 * Props:       open, fileName?, placeholders, onConfirm, onClose
 * Behavior:    Per-placeholder text input (prefilled with the detected token) with a datalist of
 *              known catalog tokens; "Apply" returns only the tokens whose value changed.
 * Data Source: none (presentational; merge-field catalog is a static constant)
 * Constraints: no API calls, no Dexie access; caller owns layout mutation + persistence.
 * Performance: small list (detected placeholders only); no virtualization needed.
 */
export default function RecreatePlaceholderReviewModal({
  open,
  fileName,
  placeholders,
  onConfirm,
  onClose,
}: RecreatePlaceholderReviewModalProps) {
  const [values, setValues] = useState<Record<string, string>>({});

  // Reset edited values whenever a new set of placeholders arrives.
  const placeholderKey = placeholders.join('|');
  const initial = useMemo(
    () => Object.fromEntries(placeholders.map((p) => [p, p])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [placeholderKey],
  );
  React.useEffect(() => {
    setValues(initial);
  }, [initial]);

  const catalogTokens = useMemo(() => DOCUMENT_MERGE_FIELDS.map((f) => f.token), []);
  const catalogLabel = useMemo(
    () => new Map(DOCUMENT_MERGE_FIELDS.map((f) => [f.token, f.label])),
    [],
  );

  if (!open) return null;

  const setValue = (token: string, next: string) =>
    setValues((prev) => ({ ...prev, [token]: next }));

  const invalidCount = placeholders.filter((p) => {
    const v = values[p];
    return v !== undefined && v.trim() !== '' && !isValidToken(v.trim());
  }).length;

  const handleApply = () => {
    const rename: Record<string, string> = {};
    for (const p of placeholders) {
      const next = (values[p] ?? p).trim();
      if (next && next !== p && isValidToken(next)) rename[p] = next;
    }
    onConfirm(rename);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="recreate-placeholders-title"
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-xs" onClick={onClose} aria-hidden="true" />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden flex flex-col max-h-[85vh]">
        <div className="px-6 pt-6 pb-3 border-b border-gray-100">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <span className="rounded-xl p-2 bg-violet-50 text-violet-600">
              <Wand2 className="h-5 w-5" />
            </span>
            <div>
              <h2 id="recreate-placeholders-title" className="text-lg font-semibold text-gray-900">
                Confirm data placeholders
              </h2>
              <p className="text-sm text-gray-500">
                {placeholders.length} placeholder{placeholders.length === 1 ? '' : 's'} detected
                {fileName ? ` in “${fileName}”` : ''}. Bind each to an inspection field or rename it.
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex flex-col gap-3">
          <datalist id="recreate-merge-field-tokens">
            {catalogTokens.map((t) => (
              <option key={t} value={t}>{catalogLabel.get(t)}</option>
            ))}
          </datalist>

          {placeholders.map((token) => {
            const current = values[token] ?? token;
            const trimmed = current.trim();
            const invalid = trimmed !== '' && !isValidToken(trimmed);
            const boundLabel = catalogLabel.get(trimmed);
            return (
              <div key={token} className="flex items-center gap-3">
                <code className="shrink-0 w-40 truncate rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700" title={mergeFieldToken(token)}>
                  {mergeFieldToken(token)}
                </code>
                <div className="flex-1 min-w-0">
                  <input
                    type="text"
                    list="recreate-merge-field-tokens"
                    value={current}
                    onChange={(e) => setValue(token, e.target.value)}
                    className={`w-full rounded-lg border px-3 py-1.5 text-sm focus:outline-hidden focus:ring-2 ${
                      invalid
                        ? 'border-red-400 focus:ring-red-300'
                        : 'border-gray-300 focus:ring-violet-300'
                    }`}
                    aria-label={`Binding for ${token}`}
                  />
                  {invalid ? (
                    <p className="mt-0.5 text-xs text-red-500">Use letters, numbers, and underscores only.</p>
                  ) : boundLabel ? (
                    <p className="mt-0.5 text-xs text-green-600">Bound to “{boundLabel}”.</p>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
          >
            Keep as-is
          </button>
          <button
            onClick={handleApply}
            disabled={invalidCount > 0}
            className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Check className="h-4 w-4" />
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
